import { useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ITEM_STATUSES,
  meetingsApi,
  type ItemStatus,
  type MeetingItem,
  type MeetingItemPatchRequest,
} from '@/api/meetings';
import type { Envelope } from '@/api/envelopes';
import { formatMoneyMinor, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import type { ItemAddress } from './itemAddress';
import { statusLabel } from '@/components/statusLabel';
import { SubmitButton } from '@/components/SubmitButton';
import { denseFieldSx } from '@/components/denseField';
import { resolveTokens } from '@/theme/theme';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { GuardedDialog } from '@/components/GuardedDialog';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';

interface PersonOption {
  userId: number;
  displayName: string;
}

interface ItemChangeDialogProps {
  item: MeetingItem;
  address: ItemAddress;
  people: PersonOption[];
  currency?: string | null | undefined;
  envelopes?: Envelope[];
  onClose: () => void;
  onSaved: () => void;
}

export function ItemChangeDialog({
  item,
  address,
  people,
  currency,
  envelopes = [],
  onClose,
  onSaved,
}: ItemChangeDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const dense = denseFieldSx(tokens);
  const allowed = ITEM_STATUSES[item.kind];
  const [body, setBody] = useState(item.body);
  const [status, setStatus] = useState<ItemStatus | ''>(item.status ?? '');
  const [ownerUserId, setOwnerUserId] = useState<number | ''>(item.ownerUserId ?? '');
  const [dueOn, setDueOn] = useState(item.dueOn ?? '');
  const [envelopeId, setEnvelopeId] = useState<number | ''>(item.envelopeId ?? '');
  const [target, setTarget] = useState(
    item.targetMinor !== null && currency
      ? formatMoneyMinor(item.targetMinor, currency, locale)
      : '',
  );
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reasonError, setReasonError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { dirty } = useDirty({ body, status, ownerUserId, dueOn, envelopeId, target, reason });
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);
  // A standalone meeting's goal is words only: no envelope, no target (MM-D-150).
  const money = address.virtualAccountId !== null;

  async function handleSave() {
    if (reason.trim().length === 0 || reason.trim().length > 500) {
      setError(t('errors.checkFields'));
      setReasonError(true);
      guard.focusErrors();
      return;
    }
    setSubmitting(true);
    setError(null);
    setReasonError(false);
    try {
      const patch: MeetingItemPatchRequest = { reason: reason.trim() };
      if (body !== item.body) patch.body = body;
      if (status !== '' && status !== item.status) patch.status = status;
      if (ownerUserId !== '' && ownerUserId !== item.ownerUserId) patch.ownerUserId = ownerUserId;
      if (dueOn !== (item.dueOn ?? '')) patch.dueOn = dueOn;
      if (item.kind === 'GOAL' && money) {
        if (envelopeId !== '' && envelopeId !== item.envelopeId) patch.envelopeId = envelopeId;
        if (currency) {
          const minor = parseMoneyToMinor(target, currency, locale);
          if (minor !== null && minor !== item.targetMinor) patch.targetMinor = minor;
        }
      }
      await meetingsApi.patchItem(
        address.virtualAccountId,
        address.meetingId,
        address.occurrenceKey,
        item.id,
        patch,
      );
      onSaved();
    } catch {
      setError(t('meetings.errorGeneric'));
      guard.focusErrors();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId="item-change"
      subtitle={<DialogSubtitle id="item-change-body">{item.body}</DialogSubtitle>}
      subtitleId="item-change-body"
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 600, maxWidth: 'calc(100% - 32px)' } } }}
    >
      <DialogTitle id="item-change">{t('items.reviseTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('items.bodyLabel')}
            value={body}
            onChange={(event) => setBody(event.target.value.slice(0, 1000))}
            multiline
            minRows={3}
            fullWidth
            sx={{
              ...dense,
              '& .MuiOutlinedInput-root': { minHeight: 88, alignItems: 'flex-start' },
            }}
          />
          <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5 }}>
            <FormControl fullWidth sx={dense}>
              <InputLabel id="item-owner">{t('items.ownerLabel')}</InputLabel>
              <Select
                labelId="item-owner"
                value={ownerUserId === '' ? '' : String(ownerUserId)}
                displayEmpty
                onChange={(event) =>
                  setOwnerUserId(event.target.value === '' ? '' : Number(event.target.value))
                }
              >
                <MenuItem value="">{t('items.ownerNone')}</MenuItem>
                {people.map((person) => (
                  <MenuItem key={person.userId} value={String(person.userId)}>
                    {person.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('items.dueOn')}
              type="date"
              value={dueOn}
              onChange={(event) => setDueOn(event.target.value)}
              fullWidth
              sx={dense}
            />
          </Stack>
          <FormControl fullWidth sx={dense}>
            <InputLabel id="item-status">{t('items.statusLabel')}</InputLabel>
            <Select
              labelId="item-status"
              value={status}
              onChange={(event) => setStatus(event.target.value as ItemStatus)}
            >
              {allowed.map((value) => (
                <MenuItem key={value} value={value}>
                  {statusLabel(t, 'meetingItem', value)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {item.kind === 'GOAL' && money && (
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1.5 }}>
              <FormControl fullWidth sx={dense}>
                <InputLabel id="item-envelope-change">{t('items.envelopeLabel')}</InputLabel>
                <Select
                  labelId="item-envelope-change"
                  value={envelopeId === '' ? '' : String(envelopeId)}
                  displayEmpty
                  onChange={(event) =>
                    setEnvelopeId(event.target.value === '' ? '' : Number(event.target.value))
                  }
                >
                  <MenuItem value="">{t('items.envelopeNone')}</MenuItem>
                  {envelopes.map((envelope) => (
                    <MenuItem key={envelope.id} value={String(envelope.id)}>
                      {envelope.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('items.targetLabel')}
                value={target}
                onChange={(event) => setTarget(event.target.value)}
                fullWidth
                sx={dense}
              />
            </Stack>
          )}
          <TextField
            label={t('items.reasonLabel')}
            value={reason}
            onChange={(event) => setReason(event.target.value.slice(0, 500))}
            error={reasonError}
            helperText={reasonError ? t('items.reasonRequired') : undefined}
            required
            fullWidth
            sx={dense}
          />
          <Typography
            sx={{
              pt: 1.5,
              borderTop: `1px solid ${tokens.line}`,
              fontSize: 12,
              color: tokens.textLabel('surfaceRaised'),
            }}
          >
            {t('items.versionNote')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={guard.requestClose}>{t('common.cancel')}</Button>
        <SubmitButton onClick={() => void handleSave()} disabled={submitting} loading={submitting}>
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </GuardedDialog>
  );
}
